import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Hammer, Plus, Trash2, Scale, CheckCircle2, AlertTriangle, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SHIFT_LABEL, shiftAt } from "@/lib/turni";

export default function EstrusioneProduzione() {
    const { user, can } = useAuth();
    const canDichiara = user?.role === "admin" || can("prod_estr.dichiara");
    const [macc, setMacc] = useState([]);
    const [ord, setOrd] = useState([]);
    const [openOrd, setOpenOrd] = useState(null); // ordine aperto (con bobine)
    const [openMac, setOpenMac] = useState(null); // macchina della card cliccata (per bobina multi-macchina)
    const [bobine, setBobine] = useState([]);
    const [dlg, setDlg] = useState(false);
    const [kg, setKg] = useState("");
    const [note, setNote] = useState("");
    // Prompt "Ordine al 100% — chiudere?" dopo l'ultima bobina
    const [askClose, setAskClose] = useState(false);

    const turnoAuto = shiftAt(new Date());

    const load = async () => {
        try {
            const [m, o] = await Promise.all([
                api.get("/macchine-estrusione"),
                api.get("/ordini-estrusione?stato=aperto"),
            ]);
            setMacc(m.data.filter(x => x.attiva));
            setOrd(o.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

    const byMacc = useMemo(() => {
        const map = {};
        for (const m of macc) map[m.codice] = [];
        for (const o of ord) {
            const macs = (o.macchine_codici && o.macchine_codici.length) ? o.macchine_codici : (o.macchina_codice ? [o.macchina_codice] : []);
            for (const mc of macs) (map[mc] ||= []).push(o);
        }
        return map;
    }, [ord, macc]);

    const openOrdine = async (o, macCode) => {
        setOpenOrd(o); setOpenMac(macCode || o.macchina_codice); setBobine([]);
        try { const { data } = await api.get(`/ordini-estrusione/${o.id}/bobine`); setBobine(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const stampaBobina = async (b, auto = false) => {
        try {
            const { data } = await api.post(`/bobine/${b.id}/stampa`);
            if (data.sent) toast.success(`${b.numero}: ${data.message}`);
            else if (auto) toast.warning(data.message);
            else {
                toast.warning(data.message);
                const a = document.createElement("a");
                a.href = URL.createObjectURL(new Blob([data.zpl], { type: "text/plain" }));
                a.download = `${b.numero}.zpl`; a.click(); URL.revokeObjectURL(a.href);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addBobina = async () => {
        if (!kg || parseFloat(kg) <= 0) { toast.error("Peso bobina obbligatorio"); return; }
        try {
            const { data } = await api.post(`/ordini-estrusione/${openOrd.id}/bobine`,
                { kg: parseFloat(kg), note: note?.trim() || null, turno: turnoAuto,
                  macchina_codice: openMac || openOrd.macchina_codice });
            toast.success(`Bobina ${data.numero} registrata (${data.kg} kg)`);
            setKg(""); setNote(""); setDlg(false);
            stampaBobina(data, true);
            // ricarica ordine + bobine
            const [{ data: fresh }, { data: bs }] = await Promise.all([
                api.get(`/ordini-estrusione/${openOrd.id}`),
                api.get(`/ordini-estrusione/${openOrd.id}/bobine`),
            ]);
            setOpenOrd(fresh); setBobine(bs); load();
            // Se il target è raggiunto/superato: chiedi conferma chiusura
            const target = fresh.kg_target || 0;
            const done = fresh.kg_prodotti || 0;
            if (fresh.stato === "aperto" && target > 0 && done >= target) {
                setAskClose(true);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doChiudi = async () => {
        try {
            const { data } = await api.post(`/ordini-estrusione/${openOrd.id}/chiudi`);
            if (data.fuori_tolleranza) {
                toast.warning(`Ordine chiuso — fuori tolleranza (${data.kg_prodotti}/${data.kg_target} kg)`);
            } else {
                toast.success(`Ordine chiuso (${data.kg_prodotti}/${data.kg_target} kg)`);
            }
            const { data: fresh } = await api.get(`/ordini-estrusione/${openOrd.id}`);
            setOpenOrd(fresh); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const delBobina = async (b) => {
        if (!window.confirm(`Eliminare bobina ${b.numero} (${b.kg} kg)?`)) return;
        try {
            await api.delete(`/bobine/${b.id}`);
            const { data: bs } = await api.get(`/ordini-estrusione/${openOrd.id}/bobine`);
            setBobine(bs);
            const { data: fresh } = await api.get(`/ordini-estrusione/${openOrd.id}`);
            setOpenOrd(fresh); load();
            toast.success("Bobina eliminata");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (openOrd) {
        const target = openOrd.kg_target || 0;
        const done = openOrd.kg_prodotti || 0;
        const prog = target > 0 ? Math.min(1, done / target) : 0;
        const overTarget = target > 0 && done >= target;
        const fuoriToll = target > 0 && (Math.abs(done - target) / target) > 0.15;
        return (
            <div className="space-y-6">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <button onClick={()=>setOpenOrd(null)} className="text-xs text-slate-400 hover:text-slate-200 mb-2" data-testid="btn-back-oe">← Torna alla lista</button>
                        <h1 className="font-display text-3xl font-extrabold text-amber-400">{openOrd.numero}</h1>
                        <div className="text-xs text-slate-400 mt-1">
                            Macchina <b className="text-slate-200">{openMac || openOrd.macchina_codice}</b>
                            {(openOrd.macchine_codici?.length > 1) && (
                                <span className="ml-2 text-[10px] text-slate-500">(ordine su {openOrd.macchine_codici.join(", ")})</span>
                            )}
                            {" · Turno "}{SHIFT_LABEL[turnoAuto] || turnoAuto}
                        </div>
                    </div>
                    {canDichiara && openOrd.stato === "aperto" && <Button onClick={()=>setDlg(true)} data-testid="btn-new-bobina" className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                        <Plus size={16} /> Registra bobina
                    </Button>}
                </div>

                <Card className="card p-5">
                    <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-mono mb-3">Ricetta di produzione</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm items-start">
                        {/* Slot 1 — grande evidenza AMBRA: Misura × Spessore × Colore */}
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                            <div className="text-[10px] uppercase tracking-widest text-amber-400/70 font-mono">Larghezza · Spessore · Colore</div>
                            <div className="mt-1 font-mono font-black text-amber-300 text-3xl leading-tight">
                                {openOrd.articolo?.larghezza_cm || "—"} <span className="text-amber-500/60 text-xl">×</span> {openOrd.articolo?.spessore_my || "—"}<span className="text-amber-500/60 text-xl">μ</span>
                            </div>
                            <div className="mt-1 text-amber-200 text-lg font-semibold">{openOrd.articolo?.colore_nome || "—"}</div>
                        </div>

                        {/* Slot 2 — PML in colore diverso (verde acqua/cyan) */}
                        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                            <div className="text-[10px] uppercase tracking-widest text-cyan-300/80 font-mono">PML (grammi)</div>
                            <div className="mt-1 font-mono font-black text-cyan-300 text-4xl leading-tight">{openOrd.articolo?.pml_gr || "—"}</div>
                            <div className="mt-1 text-cyan-200/70 text-[10px]">peso medio lineare</div>
                        </div>

                        {/* Slot 3 — Cliente + Materiale (evidenziato) */}
                        <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">Cliente</div>
                            <div className="mt-1 text-slate-100 text-lg font-semibold truncate">{openOrd.articolo?.cliente_nome || "—"}</div>
                            <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-400 font-mono">Materiale</div>
                            <div className="mt-1 text-slate-100 text-lg font-semibold truncate">{openOrd.materiale || "—"}</div>
                        </div>

                        {/* Slot 4 — Tipo estrusione + Codice (bianco basic) */}
                        <div className="p-2">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tipo estrusione</div>
                            <div className="mt-1 text-slate-100 text-lg font-semibold">{openOrd.articolo?.tipo_estrusione || "—"}</div>
                            <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono">Codice</div>
                            <div className="mt-1 text-slate-100 font-mono">{openOrd.articolo?.codice || "—"}</div>
                        </div>

                        <RecipeCell label="Additivi" value={openOrd.articolo?.additivi || "—"} span={2} />
                        <RecipeCell label="Stampa n° colori" value={openOrd.articolo?.stampa_colori || "—"} />
                        <RecipeCell label="Trattamento" value={openOrd.articolo?.trattamento || "—"} />

                        {openOrd.note && <RecipeCell label="Note ordine" value={openOrd.note} span={4} />}
                    </div>
                    <div className="mt-6">
                        <div className="flex items-end justify-between">
                            <div className="font-mono text-emerald-300 text-5xl font-black leading-none">
                                {done}
                                <span className="text-slate-500 text-5xl font-black"> / {target}</span>
                                <span className="text-emerald-400/70 text-3xl font-bold ml-2">kg</span>
                            </div>
                            <div className="text-sm text-slate-400 font-mono">{bobine.length} bobine</div>
                        </div>
                        <div className="mt-3 h-3 bg-slate-800 rounded overflow-hidden">
                            <div className={`h-full transition-all ${fuoriToll ? "bg-red-500" : (overTarget ? "bg-amber-400" : "bg-emerald-400")}`} style={{width: `${prog*100}%`}} />
                        </div>
                        {overTarget && !fuoriToll && <div className="mt-2 text-xs text-amber-300 flex items-center gap-2"><CheckCircle2 size={12} /> Target raggiunto — l'ufficio può chiudere l'ordine</div>}
                        {fuoriToll && <div className="mt-2 text-xs text-red-300 flex items-center gap-2"><AlertTriangle size={12} /> Fuori tolleranza (±15%)</div>}
                    </div>
                </Card>

                <Card className="card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-5 py-3">Bobina</th>
                                <th className="text-right px-5 py-3">Kg</th>
                                <th className="text-left px-5 py-3">Operatore</th>
                                <th className="text-left px-5 py-3">Turno</th>
                                <th className="text-left px-5 py-3">Ora</th>
                                <th className="text-right px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {bobine.length === 0 && <tr><td colSpan="6" className="px-5 py-8 text-center text-slate-500 text-xs">Nessuna bobina registrata</td></tr>}
                            {bobine.map(b => (
                                <tr key={b.id} className="border-t border-slate-800/60" data-testid={`row-bobina-${b.numero}`}>
                                    <td className="px-5 py-3 doc-code text-xs text-amber-300">{b.numero}</td>
                                    <td className="px-5 py-3 text-right font-mono text-xs text-emerald-300">{b.kg}</td>
                                    <td className="px-5 py-3 text-xs">{b.operatore_nome || "—"}</td>
                                    <td className="px-5 py-3 text-xs"><Badge className="badge-pill bg-slate-800 text-slate-300 border-slate-700">{SHIFT_LABEL[b.turno] || b.turno}</Badge></td>
                                    <td className="px-5 py-3 text-xs text-slate-500 font-mono">{b.created_at ? new Date(b.created_at).toLocaleString("it-IT") : "—"}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <button onClick={()=>stampaBobina(b)} data-testid={`btn-print-bobina-${b.numero}`} title="Stampa etichetta" className="p-2 rounded hover:bg-emerald-500/20 text-emerald-400"><Printer size={14} /></button>
                                        {canDichiara && b.stato === "magazzino" && (user?.role === "admin" || user?.role === "ufficio" || b.operatore_id === user?.id) &&
                                            <button onClick={()=>delBobina(b)} data-testid={`btn-del-bobina-${b.numero}`} className="p-2 rounded hover:bg-red-500/20 text-red-400"><Trash2 size={14} /></button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>

                <Dialog open={dlg} onOpenChange={setDlg}>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                        <DialogHeader><DialogTitle className="font-display">Registra bobina</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="text-xs text-slate-400 flex items-center gap-2 bg-slate-800/40 rounded p-3">
                                <Scale size={14} className="text-emerald-400" />
                                Bobina appena staccata su <b className="text-slate-100">{openMac || openOrd.macchina_codice}</b> · Turno {SHIFT_LABEL[turnoAuto] || turnoAuto} (auto)
                            </div>
                            <div><Label className="text-xs text-slate-400">Peso bobina (kg) *</Label>
                                <Input data-testid="input-kg-bobina" type="number" step="0.1" value={kg}
                                       onChange={e=>setKg(e.target.value)} autoFocus
                                       className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-2xl text-center" /></div>
                            <div><Label className="text-xs text-slate-400">Note (opzionale)</Label>
                                <Textarea rows={2} value={note} onChange={e=>setNote(e.target.value)}
                                          data-testid="input-note-bobina"
                                          className="bg-slate-950/50 border-slate-800 mt-1" /></div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={()=>setDlg(false)} className="bg-transparent border-slate-700 text-slate-300">Annulla</Button>
                            <Button onClick={addBobina} data-testid="btn-save-bobina" className="bg-amber-500 hover:bg-amber-400 text-slate-950">Registra</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Prompt automatico "Ordine al 100%: chiudere?" dopo l'ultima bobina */}
                <Dialog open={askClose} onOpenChange={setAskClose}>
                    <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-chiudi-oe-100">
                        <DialogHeader>
                            <DialogTitle className="font-display text-emerald-300 flex items-center gap-2">
                                <CheckCircle2 size={20} /> Ordine al 100%
                            </DialogTitle>
                        </DialogHeader>
                        <div className="text-sm text-slate-300 leading-relaxed">
                            Target raggiunto:
                            <div className="mt-2 font-mono text-emerald-300 text-xl">
                                {openOrd.kg_prodotti || 0} / {openOrd.kg_target} <span className="text-slate-500 text-base">kg</span>
                            </div>
                            <div className="mt-3">Vuoi chiudere adesso l'ordine <b className="doc-code text-amber-300">{openOrd.numero}</b>?</div>
                            {fuoriToll && <div className="mt-2 text-xs text-red-300 flex items-center gap-2"><AlertTriangle size={12} /> Fuori tolleranza (±15%)</div>}
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={()=>setAskClose(false)}
                                    data-testid="btn-oe-100-no"
                                    className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800">
                                No, continua a produrre
                            </Button>
                            <Button onClick={async ()=>{ setAskClose(false); await doChiudi(); }}
                                    data-testid="btn-oe-100-yes"
                                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
                                Sì, chiudi ordine
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-amber-400/80 flex items-center gap-2">
                    <Hammer size={12} /> POSTAZIONE OPERATORE · ESTRUSIONE
                </div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Produzione Estrusione</h1>
                <p className="mt-2 text-slate-400 text-sm max-w-2xl">Ordini aperti per macchina. Apri un ordine per registrare le bobine prodotte.</p>
                <div className="mt-2 text-xs"><Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">Turno {SHIFT_LABEL[turnoAuto] || turnoAuto} · auto</Badge></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {macc.map((m, idx) => {
                    // Palette 5 colori per le 5 macchine (ripete se >5)
                    const palettes = [
                        { border: "border-amber-500/40",  bg: "bg-amber-500/5",  text: "text-amber-400",  bar: "bg-amber-400"  },
                        { border: "border-cyan-500/40",   bg: "bg-cyan-500/5",   text: "text-cyan-300",   bar: "bg-cyan-400"   },
                        { border: "border-violet-500/40", bg: "bg-violet-500/5", text: "text-violet-300", bar: "bg-violet-400" },
                        { border: "border-emerald-500/40",bg: "bg-emerald-500/5",text: "text-emerald-300",bar: "bg-emerald-400"},
                        { border: "border-rose-500/40",   bg: "bg-rose-500/5",   text: "text-rose-300",   bar: "bg-rose-400"   },
                    ];
                    const p = palettes[idx % palettes.length];
                    return (
                    <Card key={m.codice} className={`card p-5 border ${p.border} ${p.bg}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className={`font-display font-black text-3xl ${p.text} leading-none`}>{m.codice}</div>
                                <div className="text-[11px] text-slate-400 tracking-widest uppercase mt-1">{m.descrizione || "Estrusore"}</div>
                            </div>
                            <Badge className={`badge-pill bg-slate-800 ${p.text} border-slate-700 text-sm px-3 py-1`}>{(byMacc[m.codice] || []).length}</Badge>
                        </div>
                        <div className="space-y-3">
                            {(byMacc[m.codice] || []).length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Nessun ordine aperto</div>}
                            {(byMacc[m.codice] || []).map(o => {
                                const prog = o.kg_target > 0 ? Math.min(1, (o.kg_prodotti || 0) / o.kg_target) : 0;
                                return (
                                    <button key={o.id} onClick={()=>openOrdine(o, m.codice)} data-testid={`btn-open-oe-${o.numero}`}
                                            className={`w-full text-left p-4 rounded border ${p.border} bg-slate-900/50 hover:bg-slate-900/80 hover:shadow-lg transition-all`}>
                                        <div className="flex items-center justify-between">
                                            <div className={`doc-code text-sm ${p.text} font-bold`}>{o.numero}</div>
                                            <div className={`text-sm font-mono font-bold ${p.text}`}>{o.kg_prodotti || 0}<span className="text-slate-500">/{o.kg_target}kg</span></div>
                                        </div>
                                        <div className="text-base text-slate-100 mt-2 font-semibold">
                                            {o.articolo?.larghezza_cm}<span className="text-slate-500">×</span>{o.articolo?.spessore_my}μ · {o.articolo?.colore_nome}
                                        </div>
                                        <div className="flex items-center gap-3 mt-2 text-xs">
                                            <span className="text-slate-400">PML: <b className="text-cyan-300 font-mono text-sm">{o.articolo?.pml_gr || "—"}g</b></span>
                                            {o.materiale && <span className="text-slate-400 truncate">MAT: <b className="text-slate-200">{o.materiale}</b></span>}
                                        </div>
                                        <div className="mt-3 h-2 bg-slate-800 rounded overflow-hidden">
                                            <div className={`h-full ${p.bar}`} style={{width: `${prog*100}%`}} />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </Card>
                    );
                })}
            </div>
        </div>
    );
}

function RecipeCell({ label, value, mono, strong, emerald, highlight, span = 1 }) {
    const colSpanCls = span === 2 ? "col-span-2" : span === 4 ? "col-span-2 md:col-span-4" : "";
    return (
        <div className={colSpanCls}>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">{label}</div>
            <div className={`mt-1 ${mono ? "font-mono" : ""} ${strong ? "text-amber-300 font-bold" : (emerald ? "text-emerald-300" : "text-slate-100")} ${highlight ? "bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 inline-block" : ""}`}>{value || "—"}</div>
        </div>
    );
}
